import {
  useCreateAssetDimension,
  useDeleteDimension, // Add this new hook
  useDimensionList,
  useUpdateAssetDimension,
} from "@features/ax/settings/assetClassification/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Edit, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
interface ManageDimensionsProps {
  editingDimensions: any | null;
  showManageDimensions: boolean;
  onClose: () => void;
}
export const ManageDimensions: React.FC<ManageDimensionsProps> = ({
  editingDimensions,
  showManageDimensions,
  onClose,
}) => {
  const { toast } = useToast();
  // State for edit modal
  const [editingDimension, setEditingDimension] = useState<any | null>(null);
  const [initialFormData] = useState({
    name: "",
    type: "text" as "text" | "number" | "dropdown" | "date",
    unit: "",
    required: false,
    options: "",
  });
  const { mutateAsync: createDimension, isPending: isCreating } =
    useCreateAssetDimension();
  const { mutateAsync: updateDimension, isPending: isUpdating } =
    useUpdateAssetDimension();
  const { mutateAsync: deleteDimension, isPending: isDeleting } =
    useDeleteDimension();
  const { data: dimensionsData, isLoading } = useDimensionList(
    editingDimensions?.assetTypeCode
  );
  // Memoized options for dropdown type
  const typeOptions = useMemo(
    () => [
      { value: "text", label: "Text" },
      { value: "number", label: "Number" },
      { value: "dropdown", label: "Dropdown" },
      { value: "date", label: "Date" },
    ],
    []
  );
  const [currentAddType, setCurrentAddType] = useState("");
  const [currentEditType, setCurrentEditType] = useState("");
  // Watch type fields

  // Form field definitions for ADD form
  const addFormFields: FormField[] = useMemo(() => {
    const baseFields: FormField[] = [
      {
        name: "name",
        type: "text",
        label: "Name",
        placeholder: "e.g., diameter",
        required: true,
        className: "col-span-1 mt-3",
      },
      {
        name: "type",
        type: "select",
        label: "Type",
        placeholder: "Select type",
        required: true,
        options: typeOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "required",
        type: "checkbox",
        label: "Required",
        required: false,
        className: "col-span-1 mt-12",
      },
      {
        name: "value",
        type:
          currentAddType === "date"
            ? "datepicker"
            : currentAddType === "number"
            ? "number"
            : "text",
        label: "value",
        className: "col-span-1 mt-12",
        required: false,
      },
    ];
    if (currentAddType === "dropdown") {
      baseFields.push({
        name: "options",
        type: "text",
        label: "Options (comma-separated)",
        placeholder: "e.g., Option 1, Option 2, Option 3",
        required: true,
        helperText: "Enter options separated by commas",
        className: "col-span-3 mt-3",
      });
      const valueField = baseFields.find((f) => f.name === "value");

      if (valueField) {
        valueField.helperText = "Enter a value from the dropdown options";
      }
    }
    if (currentAddType === "number") {
      baseFields.push({
        name: "unit",
        type: "text",
        label: "Unit (optional)",
        placeholder: "e.g., mm, L/s, %",
        required: false,
        className: "col-span-1 mt-12",
      });
    }
    return baseFields;
  }, [typeOptions, currentAddType]);
  // Form field definitions for EDIT form
  const editFormFields: FormField[] = useMemo(() => {
    const baseFields: FormField[] = [
      {
        name: "name",
        type: "text",
        label: "Name",
        placeholder: "e.g., diameter",
        required: true,
        className: "col-span-1 mt-3",
      },
      {
        name: "type",
        type: "select",
        label: "Type",
        placeholder: "Select type",
        required: true,
        options: typeOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "required",
        type: "checkbox",
        label: "Required",
        required: false,
        className: "col-span-1 mt-12",
      },
      {
        name: "value",
        type:
          currentAddType === "date"
            ? "datepicker"
            : currentAddType === "number"
            ? "number"
            : "text",
        label: "value",
        required: false,
        className: "col-span-1 mt-12",
      },
    ];
    if (currentEditType === "dropdown") {
      baseFields.push({
        name: "options",
        type: "text",
        label: "Options (comma-separated)",
        placeholder: "e.g., Option 1, Option 2, Option 3",
        required: true,
        helperText: "Enter options separated by commas",
        className: "col-span-3 mt-3",
      });
    }
    if (currentEditType === "number") {
      baseFields.push({
        name: "unit",
        type: "text",
        label: "Unit (optional)",
        placeholder: "e.g., mm, L/s, %",
        required: false,
        className: "col-span-1 mt-12",
      });
    }
    return baseFields;
  }, [typeOptions, currentEditType]);
  // Reset add form when dialog opens

  // Handle edit dimension click
  const handleEditDimension = (dimension: any) => {
    const editData = {
      name: dimension.name || "",
      type: dimension.dataType?.toLowerCase() || "text",
      unit: dimension.unit || "",
      required: dimension.isRequired || false,
      options: dimension.optionValue ? dimension.optionValue.join(", ") : "",
      value: dimension.value || "",
    };
    editForm.reset(editData);
    setEditingDimension(dimension);
  };
  const handleDeleteDimension = (code: string) => {
    deleteDimension(code);
  };
  // Handle edit form submission
  const handleEditSubmit = async () => {
    const data = editForm.getValues();
    const isValid = await FormService.validateForm(editForm);
    if (!isValid) {
      return;
    }
    if (!data.name?.trim()) {
      toast({
        title: "Error",
        description: "Please enter a dimension name",
        variant: "destructive",
      });
      return;
    }
    try {
      let updatedDimension: any = {
        name: data.name.trim(),
        data_type: data.type,
        is_required: data.required,
        code: editingDimension.code,
        remote_utility_id: getRemoteUtilityId(),
        asset_type_characteristics: "dimensions",
        value: data.value,
      };
      let optionList: string[] = [];

      if (data.type === "dropdown" && data.options?.trim()) {
        optionList = data.options
          .split(",")
          .map((opt: string) => opt.trim())
          .filter(Boolean);
        updatedDimension.option_value = optionList;
      }
      if (data.value) {
        if (updatedDimension.option_value?.length > 0) {
          if (updatedDimension.option_value.includes(data.value)) {
            updatedDimension.value = data.value;
          } else {
            toast({
              title: "Error",
              description: `Invalid value: ${data.value} is not in dropdown options`,
              variant: "destructive",
            });
            return;
          }
        } else {
          updatedDimension.value = data.value;
        }
      }

      if (data.unit) {
        updatedDimension.unit = data.unit;
      }

      await updateDimension(updatedDimension);
      toast({
        title: "Success",
        description: "Dimension updated successfully",
        variant: "default",
      });
      setEditingDimension(null);
    } catch (error) {
      console.log("Update dimension error:", error);
      toast({
        title: "Error",
        description: "Failed to update dimension",
        variant: "destructive",
      });
    }
  };
  // Handle add form submission
  const handleAddSubmit = async () => {
    const data = addForm.getValues();
    if (!data.name?.trim()) {
      toast({
        title: "Error",
        description: "Please enter a dimension name",
        variant: "destructive",
      });
      return;
    }
    try {
      let newDimension: any = {
        name: data.name.trim(),
        data_type: data.type,
        is_required: data.required,
        code: editingDimensions.assetTypeCode + "_" + data.name,
        remote_utility_id: getRemoteUtilityId(),
        asset_type_characteristics: "dimensions",
      };
      let optionList: string[] = [];

      if (data.type === "dropdown" && data.options?.trim()) {
        optionList = data.options
          .split(",")
          .map((opt: string) => opt.trim())
          .filter(Boolean);
        newDimension.option_value = optionList;
      }
      if (data.value) {
        if (newDimension.option_value?.length > 0) {
          if (newDimension.option_value.includes(data.value)) {
            newDimension.value = data.value;
          } else {
            toast({
              title: "Error",
              description: `Invalid value: ${data.value} is not in dropdown options`,
              variant: "destructive",
            });
            return;
          }
        } else {
          newDimension.value = data.value;
        }
      }

      if (data.unit) {
        newDimension.unit = data.unit;
      }
      await createDimension(newDimension);
      addForm.reset(initialFormData);
    } catch (error) {
      toast({
        title: `Failed to create asset dimension: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  // Handle add form reset
  const handleAddReset = () => {
    addForm.reset(initialFormData);
  };

  const handleAddFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "type") {
      setCurrentAddType(value);
    }
  };

  const handleEditFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "type") {
      setCurrentEditType(value);
    }
  };

  // Handle edit form reset
  const handleEditReset = () => {
    if (editingDimension) {
      const editData = {
        name: editingDimension.name || "",
        type: editingDimension.entityTypeDisplay?.toLowerCase() || "text",
        unit: editingDimension.unit || "",
        required: editingDimension.isRequired || false,
        options: editingDimension.options
          ? editingDimension.options.join(", ")
          : "",
      };
      editForm.reset(editData);
    }
  };
  // Dynamic form config
  const formConfig = {
    showResetButton: true,
    showSubmitButton: true,
    spacing: "comfortable" as const,
    classes: {
      form: "space-y-4 p-0",
      button: "px-4 py-2 rounded-md font-medium transition-colors",
      buttonContainer: "flex justify-end gap-3 pt-4 mt-4",
      input:
        "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
      textarea:
        "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
      select:
        "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
      label: "block text-sm font-medium text-gray-700 mb-1",
      error: "text-sm text-red-600 mt-1",
      helperText: "text-sm text-gray-500 mt-1",
      container: "space-y-1",
      checkbox: "mr-2",
    },
  };
  // Create form instance for ADD form
  const addForm = FormService.createForm({
    fields: addFormFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: initialFormData,
  });

  // Create form instance for EDIT form
  const editForm = FormService.createForm({
    fields: editFormFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: initialFormData,
  });

  useEffect(() => {
    if (editingDimensions) {
      addForm.reset(initialFormData);
    }
  }, [editingDimensions, addForm]);

  return (
    <>
      {/* Main Manage Dimensions Modal */}
      <Dialog open={showManageDimensions} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Manage Dimensions for: {editingDimensions?.assetType} (
              {editingDimensions?.assetClass})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium mb-4">Add New Dimension</h4>
              <DynamicForm
                key={`dimension-form-${currentAddType}`}
                fields={addFormFields}
                form={addForm}
                onSubmit={handleAddSubmit}
                onReset={handleAddReset}
                onFieldChange={handleAddFieldChange}
                gridColumns={3}
                responsiveColumns={{
                  sm: 1,
                  md: 2,
                  lg: 3,
                }}
                config={{
                  ...formConfig,
                  submitButtonText: isCreating ? "Adding..." : "Add Dimension",
                  resetButtonText: "Clear",
                }}
              />
            </div>
            {/* Existing Dimensions */}
            <div className="space-y-4">
              <h4 className="font-medium">
                Existing Dimensions ({dimensionsData?.length || 0})
              </h4>
              {dimensionsData?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No dimensions defined yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {dimensionsData?.map((dimension: any) => (
                    <div
                      key={dimension.id}
                      className="border rounded-lg p-4 flex items-start justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h5 className="font-medium">{dimension.name}</h5>
                          <Badge
                            variant={
                              dimension.isRequired ? "default" : "secondary"
                            }
                          >
                            {dimension.isRequired ? "Required" : "Optional"}
                          </Badge>
                          <Badge variant="outline">{dimension.dataType}</Badge>
                          {dimension.value && (
                            <Badge variant="outline">{dimension.value}</Badge>
                          )}
                          {dimension.unit && (
                            <Badge variant="outline">{dimension.unit}</Badge>
                          )}
                        </div>
                        {dimension.optionValue &&
                          dimension.optionValue.length > 0 && (
                            <p className="text-sm text-gray-600">
                              Options: {dimension.optionValue.join(", ")}
                            </p>
                          )}
                        {dimension.description && (
                          <p className="text-sm text-gray-600 mt-1">
                            {dimension.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditDimension(dimension)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isDeleting}
                          onClick={() => handleDeleteDimension(dimension.code)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Edit Dimension Modal */}
      <Dialog
        open={!!editingDimension}
        onOpenChange={() => setEditingDimension(null)}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Dimension: {editingDimension?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium mb-4">Edit Dimension Details</h4>
              <DynamicForm
                key={`edit-dimension-form-${currentEditType}-${editingDimension?.id}`}
                fields={editFormFields}
                form={editForm}
                onSubmit={handleEditSubmit}
                onReset={handleEditReset}
                onFieldChange={handleEditFieldChange}
                gridColumns={3}
                responsiveColumns={{
                  sm: 1,
                  md: 2,
                  lg: 3,
                }}
                config={{
                  ...formConfig,
                  submitButtonText: isUpdating
                    ? "Updating..."
                    : "Update Dimension",
                  resetButtonText: "Reset",
                }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
